import { useState, useEffect } from "react";
import { createRoot } from "react-dom/client";
import InlinePartnerList from "./InlinePartnerList";
import PartnerGrid from "./PartnerGrid";
import OpportunityCard from "./OpportunityCard";
import { useDisplayMode, useTheme, useDeviceType } from "./hooks/useOpenAiGlobal";
import { Toaster } from "@/components/ui/toaster";
import { ErrorBoundary } from "./ErrorBoundary";
import { Partner, Opportunity } from "./types";

const LOADING_MESSAGES = [
  "Finding the perfect partners for you...",
  "Did you know? Nesha's synergy engine has 56K+ companies and grows every month.",
  "Scanning 56K+ companies to find your top matches right now...",
  "Analyzing partnership potential and compatibility scores...",
  "Almost there! Ranking the best opportunities for you...",
];

function LoadingMessages() {
  const [messageIndex, setMessageIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // setInterval: Runs this callback repeatedly every 5000ms (5 seconds)
    const interval = setInterval(() => {
      // Start fade-out transition
      setIsVisible(false);

      // setTimeout: Wait 300ms for fade-out animation to complete, then swap message
      setTimeout(() => {
        setMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
        // Fade back in with new message
        setIsVisible(true);
      }, 300);
    }, 7000); // Increased from 3500ms for easier reading

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="bg-card rounded-xl shadow-lg p-5 mb-4">
      <div className="flex items-center gap-3">
        <p
          className={`text-sm text-foreground transition-opacity duration-300 ${isVisible ? "opacity-100" : "opacity-0"
            }`}
        >
          {LOADING_MESSAGES[messageIndex]}
        </p>
      </div>
    </div>
  );
}

function LoadingBlock() {
  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";

  return (
    <div className="border border-border rounded-lg p-4 animate-pulse">
      {/* Desktop layout */}
      {!isMobile && (
        <div className="flex flex-row items-start justify-between gap-4">
          {/* Left content - name, tags, description */}
          <div className="flex-1 min-w-0">
            {/* Name placeholder */}
            <div className="h-5 rounded w-1/3 bg-muted mb-2" />

            {/* Tags */}
            <div className="flex flex-wrap gap-1.5 mt-2">
              <div className="h-6 rounded-full w-16 bg-muted" />
              <div className="h-6 rounded-full w-20 bg-muted" />
              <div className="h-6 rounded-full w-14 bg-muted" />
            </div>

            {/* Description */}
            <div className="space-y-2 mt-2">
              <div className="h-4 rounded w-full bg-muted" />
              <div className="h-4 rounded w-5/6 bg-muted" />
            </div>
          </div>

          {/* Right content - score badge + button (bottom aligned) */}
          <div className="flex flex-col items-end justify-between flex-shrink-0 self-stretch">
            {/* Score badge */}
            <div className="w-11 h-11 rounded-full bg-muted flex-shrink-0" />
            {/* Button */}
            <div className="h-9 rounded-lg w-24 bg-muted" />
          </div>
        </div>
      )}

      {/* Mobile layout */}
      {isMobile && (
        <div className="flex flex-col gap-3">
          {/* Name + Badge row */}
          <div className="flex items-start justify-between">
            <div className="h-5 rounded w-1/2 bg-muted" />
            <div className="w-10 h-10 rounded-full bg-muted flex-shrink-0" />
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5">
            <div className="h-6 rounded-full w-16 bg-muted" />
            <div className="h-6 rounded-full w-20 bg-muted" />
            <div className="h-6 rounded-full w-14 bg-muted" />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <div className="h-4 rounded w-full bg-muted" />
            <div className="h-4 rounded w-5/6 bg-muted" />
            <div className="h-4 rounded w-4/6 bg-muted" />
          </div>

          {/* Full-width button */}
          <div className="h-9 rounded-lg w-full bg-muted" />
        </div>
      )}
    </div>
  );
}

export function App() {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [requestorDomain, setRequestorDomain] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const displayMode = useDisplayMode();
  const theme = useTheme();
  const deviceType = useDeviceType();
  const isMobile = deviceType === "mobile";

  // Apply theme class to document for CSS variables
  useEffect(() => {
    if (theme === "dark") {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  }, [theme]);

  useEffect(() => {
    // 🟢 SAFETY NET: REDIRECT IF NOT IN IFRAME
    // If window.top === window.self, the user is not in the widget (iframe).
    // This happens if they click "Open in..." OR visit widgets.nesha.ai directly.
    if (window.top === window.self) {
      console.log("Direct visit detected. Redirecting to nesha.ai...");
      setIsRedirecting(true);
      window.location.href = "https://nesha.ai";
    }
  }, []);

  useEffect(() => {
    const updateContent = () => {
      const toolOutput = (window as any).openai?.toolOutput;

      if (toolOutput?.companies) {
        setPartners(toolOutput.companies);
        setOpportunities([]);
        if (toolOutput.companyUrl) {
          setRequestorDomain(toolOutput.companyUrl);
        }
      } else if (toolOutput?.opportunities) {
        setOpportunities(toolOutput.opportunities);
        setPartners([]);
      }
    };

    updateContent();
    const interval = setInterval(updateContent, 500);
    return () => clearInterval(interval);
  }, []);

  const handleShowMore = async () => {
    try {
      await (window as any).openai?.requestDisplayMode?.({ mode: "fullscreen" });
    } catch (error) {
      console.error("Failed to request fullscreen mode:", error);
    }
  };

  const handleEmailSubmitted = (email: string) => {
    setUserEmail(email);
  };

  useEffect(() => {
    // 🟢 PUNCH-OUT HACK
    // This tricks the OpenAI header button to link to "widgets.nesha.ai/home"
    // instead of just "widgets.nesha.ai/"
    try {
      const nativeReplaceState = History.prototype.replaceState.bind(history);
      // We use /home as the trigger path
      nativeReplaceState({}, "", "/home");
    } catch (e) {
      console.error("Failed to update punch-out url", e);
    }
  }, []);

  const hasPartners = partners.length > 0;
  const hasOpportunities = opportunities.length > 0;
  const isFullscreen = displayMode === "fullscreen";

  // If redirecting, hide the UI to prevent "flashing" the widget before bounce
  if (isRedirecting) {
    return null;
  }

  return (
    <>
      <div className="antialiased w-full">

        <div className={`max-w-5xl mx-auto ${isMobile ? "px-0" : ""}`}>
          {hasPartners ? (
            isFullscreen ? (
              <PartnerGrid
                partners={partners}
                requestorDomain={requestorDomain || undefined}
                userEmail={userEmail || undefined}
                onEmailSubmitted={handleEmailSubmitted}
              />
            ) : (
              <InlinePartnerList
                partners={partners}
                requestorDomain={requestorDomain || undefined}
                userEmail={userEmail || undefined}
                onEmailSubmitted={handleEmailSubmitted}
                onShowMore={handleShowMore}
              />
            )
          ) : hasOpportunities ? (
            <div className="bg-card rounded-xl shadow-lg animate-slide-up">
              <div className="mb-5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">
                  Nesha
                </p>
                <h2 className="text-xl font-semibold text-foreground">
                  Partnership opportunities
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose an opportunity to continue.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {opportunities.map((opportunity) => (
                  <div key={opportunity.id} className="w-full">
                    <OpportunityCard
                      opportunity={opportunity}
                      fullWidth
                      onChoose={(op) => {
                        (window as any).openai?.sendFollowUpMessage?.({
                          prompt: `I chose: ${op.name}`,
                        });
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={`${isMobile ? "mx-auto max-w-sm" : ""}`}>
              <LoadingMessages />
              <div className={`space-y-3 ${isMobile ? "flex flex-col items-center px-4" : ""}`}>
                <div className={isMobile ? "w-full" : "w-full"}>
                  <LoadingBlock />
                </div>
                <div className={isMobile ? "w-full" : "w-full"}>
                  <LoadingBlock />
                </div>
                <div className={isMobile ? "w-full" : "w-full"}>
                  <LoadingBlock />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Toaster />
    </>
  );
}

const root = document.getElementById("nesha-root");
if (root) {
  createRoot(root).render(
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  );
}

export default App;